<script setup>
import Header from './components/SddsHeader';
import Footer from './components/SddsFooter';
import CookieFooter from './components/CookieFooter';
import { onMounted, computed, watch } from 'vue';
import { useMainStore } from './store';
import { useRouter } from 'vue-router';
import PageSpinner from './components/PageSpinner';
import StickySupportButton from './components/StickySupportButton.vue';
import SddsToastWrapper from './components/SddsToastWrapper';
import { datadogRum } from '@datadog/browser-rum';
import WebpDetector from './components/WebpDetector.vue';

const store = useMainStore();
const router = useRouter();
const market = computed(() => store.market);
const showLanguageSelection = computed(() => store.activeMarket?.showLanguageSelection);

onMounted(() => {
  const adobeLaunchScriptUrl = process.env.VUE_APP_DS_ADOBE_LAUNCH_SCRIPT_URL;
  if (adobeLaunchScriptUrl) {
    let tag = document.head.querySelector(`[src="${adobeLaunchScriptUrl}"`);
    if (!tag) {
      tag = document.createElement("script");
      tag.setAttribute("src", adobeLaunchScriptUrl);
      tag.setAttribute("async", "");
      document.head.appendChild(tag);
    }
    window.adobeDataLayer = window.adobeDataLayer || [];
  }
});
watch(market, async () => {
  datadogRum?.setGlobalContextProperty('market', market.value);
});
</script>
<template>
  <div class="wrapper-container">
    <sdds-theme />
    <WebpDetector />
    <template v-if="store.appLoading">
      <PageSpinner />
    </template>
    <template v-if="!store.appLoading">
      <Header
        :show-admin-tools="false || store.isAdmin"
        :show-language-selection="showLanguageSelection || store.isAdmin || store.isSuperUser"
      />
      <main class="main-container main-container__bottom-padding">
        <router-view />
        <StickySupportButton />
        <div class="sdds-toast-wrapper">
          <SddsToastWrapper />
        </div>
      </main>
      <CookieFooter />
      <Footer class="footer" />
    </template>
  </div>
</template>

<style scoped>
.wrapper-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}
.footer {
  margin-top: auto;
}
.main-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.main-container__bottom-padding {
  padding-bottom: var(--sdds-spacing-element-16);
}
</style>

<style>
p,
ul {
  font-size: 1.2em;
  line-height: 1.8em;
}

a {
  color: var(--sdds-blue);
  font-weight: 500;
  letter-spacing: 0.02em;
  text-decoration: none;
  border-bottom: 2px solid;
}

em {
  font-style: inherit;
  color: v-bind('store.activeMarket?.highlightColor');
}

.flex-center {
  display: flex;
  place-content: center;
}

.sdds-toast-wrapper {
  position: fixed;
  bottom: 0.5em;
  right: 0.5em;
  z-index: 20;
}
/* SDDS  */

:root,
html {
  --sdds-modal-bg: var(--sdds-grey-50) !important;

  --sdds-btn-primary-background: var(--sdds-blue);
  --sdds-btn-primary-color-hover: var(--sdds-white) !important;
  --sdds-btn-primary-border-color-hover: var(--sdds-blue-600) !important;
  --sdds-btn-primary-background-disabled: var(--sdds-grey-100) !important;
  --sdds-btn-primary-border-color-disabled: var(--sdds-blue) !important;
  --sdds-btn-primary-color-disabled: var(--sdds-blue) !important;
  --sdds-toggle-slider-width: 44px;
  --sdds-toggle-slider-height: 24px;
  --sdds-toggle-slider-sm-width: 28px;
  --sdds-toggle-slider-sm-height: 16px;
  --sdds-toggle-slider-border-radius: 16px;
  --sdds-toggle-switch: #fff;
  --sdds-toggle-switch-disabled: #dbdfe6;
  --sdds-toggle-headline: #56657a;
  --sdds-toggle-headline-disabled: #cdd1db;
  --sdds-toggle-on-slider: #1dab8b;
  --sdds-toggle-on-slider-hover: #1dab8b;
  --sdds-toggle-on-slider-focus: #1dab8b;
  --sdds-toggle-on-slider-pressed: #1dab8b;
  --sdds-toggle-on-border-focus: #1dab8b;
  --sdds-toggle-slider-disabled: #cdd1db;
  --sdds-toggle-label-colour: #1d2229;
  --sdds-toggle-label-colour-disabled: #cdd1db;
  --sdds-toggle-off-slider: #b0b7c4;
  --sdds-toggle-off-slider-hover: #868fa2;
  --sdds-toggle-off-slider-focus: #868fa2;
  --sdds-toggle-off-slider-pressed: #56657a;
  --sdds-toggle-off-border-focus: #37414f;
}

.sdds-btn {
  font-weight: 600;
}

sdds-modal > div[slot='sdds-modal-body'] {
  max-height: 70vh;
  overflow-x: auto;
}

sdds-modal > h3[slot='sdds-modal-headline'] {
  margin: 0;
}

sdds-modal > button[slot='sdds-modal-actions'] {
  margin-top: var(--sdds-spacing-layout-16);
}

.sdds-modal-overflow {
  padding-right: 16px;
}

.year-end-offer-price {
  color: #da2b40;
  font-weight: bold;
}

.show-discounted-price {
  color: #1DAB8B;
}

.show-discounted-price .discounted-price-suffix, .show-non-discounted-price {
  font-size: 0.4em;
  vertical-align: middle;
}

.margin-top-16 {
  margin-top: var(--sdds-spacing-layout-16);
}

.margin-top-24 {
  margin-top: var(--sdds-spacing-layout-24);
}

.margin-top-32 {
  margin-top: var(--sdds-spacing-layout-32);
}

.margin-top-48 {
  margin-top: var(--sdds-spacing-layout-48);
}

.margin-top-64 {
  margin-top: var(--sdds-spacing-layout-64);
}

.margin-top-96 {
  margin-top: var(--sdds-spacing-layout-96);
}

.margin-bottom-16 {
  margin-bottom: var(--sdds-spacing-layout-16);
}

.margin-bottom-24 {
  margin-bottom: var(--sdds-spacing-layout-24);
}

.margin-bottom-32 {
  margin-bottom: var(--sdds-spacing-layout-32);
}

.margin-bottom-48 {
  margin-bottom: var(--sdds-spacing-layout-48);
}

.margin-bottom-64 {
  margin-bottom: var(--sdds-spacing-layout-64);
}

.margin-bottom-96 {
  margin-bottom: var(--sdds-spacing-layout-96);
}

.padding-top-16 {
  padding-top: var(--sdds-spacing-layout-16);
}

.padding-top-24 {
  padding-top: var(--sdds-spacing-layout-24);
}

.padding-top-32 {
  padding-top: var(--sdds-spacing-layout-32);
}

.padding-top-48 {
  padding-top: var(--sdds-spacing-layout-48);
}

.padding-top-64 {
  padding-top: var(--sdds-spacing-layout-64);
}

.padding-top-96 {
  padding-top: var(--sdds-spacing-layout-96);
}

.padding-left-16 {
  padding-left: var(--sdds-spacing-layout-16);
}

.padding-left-24 {
  padding-left: var(--sdds-spacing-layout-24);
}

.padding-left-32 {
  padding-left: var(--sdds-spacing-layout-32);
}

.padding-left-48 {
  padding-left: var(--sdds-spacing-layout-48);
}

.padding-left-64 {
  padding-left: var(--sdds-spacing-layout-64);
}

.padding-left-96 {
  padding-left: var(--sdds-spacing-layout-96);
}

.el-margin-left-8 {
  margin-left: var(--sdds-spacing-element-8);
}

.el-margin-left-16 {
  margin-left: var(--sdds-spacing-element-16);
}

.el-margin-right-8 {
  margin-right: var(--sdds-spacing-element-8);
}

.el-margin-right-16 {
  margin-right: var(--sdds-spacing-element-16);
}

.el-margin-top-8 {
  margin-top: var(--sdds-spacing-element-8);
}

.el-margin-top-16 {
  margin-top: var(--sdds-spacing-element-16);
}

.el-margin-bottom-8 {
  margin-bottom: var(--sdds-spacing-element-8);
}

.el-margin-bottom-16 {
  margin-bottom: var(--sdds-spacing-element-16);
}
</style>
